import { calloutInfoAt, calloutMask, CALLOUT_CELLS, hasLowerLevel, mapInfo, radarToWorld, type Callout, type Level, type MapId } from "@69ly/maps";
import { MAX_BACKUP_SPOTS, PREFERRED_AREAS, type MemberPosition, type MemberPositionInput, type PositionSpot, type PreferredArea, type TeamDetail } from "@69ly/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api.ts";
import { useCallouts } from "../lib/callouts.ts";
import { positionsKey, usePositions } from "../lib/positions.ts";
import { errorText } from "../lib/errors.ts";
import { Avatar } from "./Avatar.tsx";
import { Icon } from "./Icon.tsx";
import { SiteOutline } from "./PlantBits.tsx";
import { Select, type SelectGroup } from "./Select.tsx";
import { AtPoint, Radar } from "./Radar.tsx";

type SideKey = "t" | "ct";
const SIDES: SideKey[] = ["t", "ct"];
const SIDE_KEY = "69ly.positions.side";
/** One colour per player, in the team's order: their face's ring and their spots on the map. */
const COLOURS = ["#ff6b6b", "#4dabf7", "#ffd43b", "#69db7c", "#da77f2", "#ffa94d", "#38d9a9", "#f783ac", "#a9e34b", "#74c0fc"];

/** Saves at once on the page (a face dropped on the map moves there now), then checks with the server. */
function useSavePosition(teamId: string, meId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MemberPositionInput) => api<MemberPosition | null>("PUT", `/teams/${teamId}/positions`, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: positionsKey(teamId) });
      const before = qc.getQueryData<MemberPosition[]>(positionsKey(teamId));
      const userId = body.userId ?? meId ?? "";
      const same = (p: MemberPosition) => p.userId === userId && p.map === body.map && p.side === body.side;
      const next: MemberPosition = { userId, map: body.map, side: body.side, area: body.area, spot: body.spot ?? "", spotKey: body.spot ? (body.spotKey ?? null) : null, backups: body.backups ?? [] };
      const empty = !next.area && !next.spot && next.backups.length === 0;
      qc.setQueryData<MemberPosition[]>(positionsKey(teamId), (old) => [...(old ?? []).filter((p) => !same(p)), ...(empty ? [] : [next])]);
      return { before };
    },
    onError: (_e, _b, ctx) => ctx?.before && qc.setQueryData(positionsKey(teamId), ctx.before),
    onSettled: () => qc.invalidateQueries({ queryKey: positionsKey(teamId) }),
  });
}

/** A choice kept in this browser. */
function useStored<T extends string>(key: string, ok: (v: string | null) => v is T, fallback: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      // A link may name it (the library's "From my spot" opens the side it was about).
      const asked = key === SIDE_KEY ? new URLSearchParams(window.location.search).get("side") : null;
      if (ok(asked)) return asked;
      const saved = window.localStorage.getItem(key);
      return ok(saved) ? saved : fallback;
    } catch {
      return fallback;
    }
  });
  const set = (v: T) => {
    setValue(v);
    try {
      window.localStorage.setItem(key, v);
    } catch {
      // No storage: the choice lasts for this visit.
    }
  };
  return [value, set];
}
const isSide = (v: string | null): v is SideKey => v === "t" || v === "ct";

/** A callout of the map, with its floor: what a spot points to. */
interface Place extends Callout {
  level: Level;
  /** "<floor>|<callout key>", as saved. */
  id: string;
}
const siteOf = (p: Pick<Place, "area">): PreferredArea | null => (p.area === "other" ? null : p.area);

/** A height on a floor, for finding the callout under a point there. */
const floorZ = (map: MapId, level: Level) => (level === "lower" ? (mapInfo(map).lowerBelowZ ?? 0) - 200 : null);

/** The spots on the map, tinted in each player's colour: main spots strong, backups faint. */
function Tints({ map, level, fills, edits }: { map: MapId; level: Level; fills: { key: string; colour: string; main: boolean }[]; edits: ReturnType<ReturnType<typeof useCallouts>["edits"]> }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const signature = fills.map((f) => `${f.key}${f.colour}${f.main}`).join(",");
  useEffect(() => {
    const c = canvas.current;
    const g = c?.getContext("2d");
    if (!c || !g) return;
    g.clearRect(0, 0, CALLOUT_CELLS, CALLOUT_CELLS);
    // Backups first: a main spot over someone's backup wins.
    for (const f of [...fills].sort((a, b) => Number(a.main) - Number(b.main))) {
      const mask = calloutMask(map, level, f.key, edits);
      g.fillStyle = f.colour;
      g.globalAlpha = f.main ? 0.42 : 0.16;
      for (let i = 0; i < mask.length; i++) if (mask[i]) g.fillRect(i % CALLOUT_CELLS, Math.floor(i / CALLOUT_CELLS), 1, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, level, signature, edits]);
  return <canvas ref={canvas} className="pos-tints" width={CALLOUT_CELLS} height={CALLOUT_CELLS} aria-hidden="true" />;
}

interface Drag {
  userId: string;
  /** Moving a backup (its key), else the main spot. */
  backup: string | null;
  x: number;
  y: number;
  over: Place | null;
}

/**
 * Who plays where, map by map and side by side: the radar with each player's
 * face on their spot and the spot tinted in their colour, the players beside it.
 * A spot is one of the map's callouts: drag a face onto the map, click the map
 * and pick who, or choose from the list. Its site comes from the callout. Each
 * player has a main spot and up to two backups. Everyone places only themselves.
 * The map comes from the Team page's map bar.
 */
export function TeamPositions({ team, meId, map }: { team: TeamDetail; meId: string | undefined; map: MapId }) {
  const { t } = useTranslation();
  const [side, setSide] = useStored<SideKey>(SIDE_KEY, isSide, "ct");
  const [level, setLevel] = useState<Level>("upper");
  const positions = usePositions(team.id);
  const save = useSavePosition(team.id, meId);
  const callouts = useCallouts();
  const frame = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [pick, setPick] = useState<{ u: number; v: number; place: Place } | null>(null);

  useEffect(() => {
    setLevel("upper");
    setPick(null);
  }, [map, side]);

  const edits = callouts.edits(map);
  // Every callout of the map, both floors, by saved key.
  const places = useMemo(() => {
    const levels: Level[] = hasLowerLevel(map) ? ["upper", "lower"] : ["upper"];
    return levels.flatMap((l) => callouts.all(map, l).filter((c) => !c.hidden).map((c): Place => ({ ...c, level: l, id: `${l}|${c.key}` })));
  }, [callouts, map]);
  const byId = useMemo(() => new Map(places.map((p) => [p.id, p])), [places]);
  // A spot written by hand before 0.9.1 points to the callout of that name, when there is one.
  const placeOf = (key: string | null, name: string) => (key ? byId.get(key) : places.find((p) => p.name === name)) ?? null;
  const placeAt = (u: number, v: number): Place | null => {
    const w = radarToWorld(map, { u, v });
    const c = calloutInfoAt(map, w.x, w.y, floorZ(map, level), edits);
    return c ? (byId.get(`${level}|${c.key}`) ?? null) : null;
  };
  // The list's choices: by site, then the rest.
  const grouped = useMemo(() => {
    const unique = (ps: Place[]) => ps.filter((p, i) => ps.findIndex((q) => q.name === p.name && q.level === p.level) === i).sort((a, b) => a.name.localeCompare(b.name));
    return (["a", "mid", "b", "other"] as const).map((a) => ({ area: a, places: unique(places.filter((p) => p.area === a)) })).filter((g) => g.places.length);
  }, [places]);

  // The list's choices for the dropdowns: by site, the lower floor said, the main spot off for a backup.
  const spotGroups = (off: string | null): SelectGroup[] =>
    grouped.map((g) => ({
      label: t(`area.${g.area}`),
      options: g.places.map((pl) => ({ value: pl.id, label: pl.name, hint: pl.level === "lower" ? t("level.lower") : undefined, disabled: pl.id === off })),
    }));

  const onSide = (positions.data ?? []).filter((p) => p.map === map && p.side === side);
  const posOf = (userId: string) => onSide.find((p) => p.userId === userId);
  const colourOf = (userId: string) => COLOURS[Math.max(0, team.members.findIndex((m) => m.userId === userId)) % COLOURS.length];
  // Everyone places themselves, owners and captains included.
  const canEdit = (userId: string) => userId === meId;
  const nameOf = (userId: string) => team.members.find((m) => m.userId === userId)?.name ?? "?";

  const put = (userId: string, change: { main?: Place | null; backups?: PositionSpot[] }) => {
    const now = posOf(userId);
    const main = change.main !== undefined ? change.main : now ? placeOf(now.spotKey, now.spot) : null;
    const keepTyped = change.main === undefined && now && !main ? now.spot : "";
    let backups = change.backups ?? now?.backups ?? [];
    if (main) backups = backups.filter((b) => b.key !== main.id);
    save.mutate({
      map,
      side,
      area: main ? siteOf(main) : keepTyped ? (now?.area ?? null) : null,
      spot: main ? main.name : keepTyped,
      spotKey: main ? main.id : null,
      backups,
      ...(userId !== meId ? { userId } : {}),
    });
  };
  const setMain = (userId: string, place: Place) => put(userId, { main: place });
  const addBackup = (userId: string, place: Place) => {
    const now = posOf(userId);
    const main = now ? placeOf(now.spotKey, now.spot) : null;
    if (main?.id === place.id) return;
    const rest = (now?.backups ?? []).filter((b) => b.key !== place.id);
    put(userId, { backups: [...rest, { key: place.id, name: place.name }].slice(-MAX_BACKUP_SPOTS) });
  };

  // ---- dragging a face onto the map
  const layerPoint = (x: number, y: number) => {
    const layer = frame.current?.querySelector<HTMLElement>(".radar-layer");
    const box = frame.current?.getBoundingClientRect();
    if (!layer || !box || x < box.left || x > box.right || y < box.top || y > box.bottom) return null;
    const r = layer.getBoundingClientRect();
    return { u: (x - r.left) / r.width, v: (y - r.top) / r.height };
  };
  const startDrag = (e: ReactPointerEvent<HTMLElement>, userId: string, backup: string | null = null) => {
    if (!canEdit(userId) || e.button > 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setPick(null);
    setDrag({ userId, backup, x: e.clientX, y: e.clientY, over: null });
  };
  const moveDrag = (e: ReactPointerEvent<HTMLElement>) => {
    if (!drag) return;
    const p = layerPoint(e.clientX, e.clientY);
    setDrag({ ...drag, x: e.clientX, y: e.clientY, over: p ? placeAt(p.u, p.v) : null });
  };
  const endDrag = (e: ReactPointerEvent<HTMLElement>) => {
    if (!drag) return;
    e.stopPropagation();
    const d = drag;
    setDrag(null);
    if (!d.over) return;
    if (d.backup) {
      const now = posOf(d.userId);
      put(d.userId, { backups: (now?.backups ?? []).map((b) => (b.key === d.backup ? { key: d.over!.id, name: d.over!.name } : b)).filter((b, i, all) => all.findIndex((x) => x.key === b.key) === i) });
    } else setMain(d.userId, d.over);
  };
  const dragProps = (userId: string, backup: string | null = null) =>
    canEdit(userId)
      ? {
          onPointerDown: (e: ReactPointerEvent<HTMLElement>) => startDrag(e, userId, backup),
          onPointerMove: moveDrag,
          onPointerUp: endDrag,
          onPointerCancel: () => setDrag(null),
          onClick: (e: { stopPropagation: () => void }) => e.stopPropagation(),
        }
      : {};

  // ---- what the map shows on this floor
  const shown = team.members.flatMap((m) => {
    const p = posOf(m.userId);
    if (!p) return [];
    const main = placeOf(p.spotKey, p.spot);
    return [
      ...(main ? [{ userId: m.userId, place: main, main: true, backup: null as string | null }] : []),
      ...p.backups.flatMap((b) => {
        const place = byId.get(b.key);
        return place ? [{ userId: m.userId, place, main: false, backup: b.key }] : [];
      }),
    ];
  });
  const here = shown.filter((s) => s.place.level === level);
  const spots = [...new Set(here.map((s) => s.place.id))].map((id) => ({ place: byId.get(id)!, who: here.filter((s) => s.place.id === id).sort((a, b) => Number(b.main) - Number(a.main)) }));
  // Coverage: each player counted at their main spot's site.
  const mains = shown.filter((s) => s.main);
  const count = (a: PreferredArea) => mains.filter((s) => siteOf(s.place) === a).length;
  const unset = team.members.filter((m) => !mains.some((s) => s.userId === m.userId)).length;
  const empty = (["a", "b"] as const).filter((a) => count(a) === 0);
  const editable = team.members.filter((m) => canEdit(m.userId));
  const sideName = t(`sideShort.${side}`);

  return (
    <section className="card team-positions" aria-labelledby="positions-title">
      <div className="positions-head">
        <div>
          <h2 id="positions-title" className="eyebrow">
            {t("positions.title")}
          </h2>
          <p className="muted small">{t("positions.help")}</p>
        </div>
        <div className="segmented positions-sides" role="group" aria-label={t("positions.side")}>
          {SIDES.map((s) => (
            <button key={s} type="button" className={`seg side-seg-${s}`} aria-pressed={side === s} onClick={() => setSide(s)}>
              {t(`sideShort.${s}`)}
            </button>
          ))}
        </div>
      </div>

      {positions.error ? (
        <p className="field-error small">{errorText(t, positions.error)}</p>
      ) : (
        <div className="positions-body">
          <div className="positions-map map-frame" ref={frame}>
            <Radar
              map={map}
              mapName={mapInfo(map).name}
              level={level}
              onPick={(p) => {
                if (editable.length === 0) return;
                const place = placeAt(p.u, p.v);
                setPick(place ? { u: p.u, v: p.v, place } : null);
              }}
            >
              <Tints
                map={map}
                level={level}
                edits={edits}
                fills={[
                  ...here.map((s) => ({ key: s.place.key, colour: colourOf(s.userId), main: s.main })),
                  // Where the face being dragged would land.
                  ...(drag?.over && drag.over.level === level ? [{ key: drag.over.key, colour: colourOf(drag.userId), main: true }] : []),
                ]}
              />
              {empty.map((a) => (
                <SiteOutline key={a} map={map} site={a} level={level} active={false} tone="warn" />
              ))}
              {spots.map(({ place, who }) => (
                <AtPoint key={place.id} u={place.u} v={place.v} className="pos-spot-faces">
                  <span className="pos-faces">
                    {who.map((w) => (
                      <span
                        key={`${w.userId}${w.backup ?? ""}`}
                        className={`pos-face${w.main ? "" : " is-backup"}${canEdit(w.userId) ? " is-draggable" : ""}`}
                        style={{ ["--who" as string]: colourOf(w.userId) }}
                        title={`${nameOf(w.userId)} · ${place.name}${w.main ? "" : ` (${t("positions.backup")})`}`}
                        {...dragProps(w.userId, w.backup)}
                      >
                        <Avatar name={nameOf(w.userId)} url={team.members.find((m) => m.userId === w.userId)?.avatarUrl} size={w.main ? 30 : 22} />
                      </span>
                    ))}
                  </span>
                </AtPoint>
              ))}
              {pick && (
                <AtPoint u={pick.u} v={pick.v} className="pos-pick-spot">
                  <div
                    className={`pos-pick glass${pick.v > 0.55 ? " is-up" : ""}${pick.u > 0.6 ? " is-left" : ""}`} role="dialog" aria-label={t("positions.whoHere", { place: pick.place.name })} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                    <div className="pos-pick-head">
                      <b>{pick.place.name}</b>
                      {siteOf(pick.place) && <span className={`pos-area area-${siteOf(pick.place)}`}>{t(`areaShort.${siteOf(pick.place)}`)}</span>}
                      <button type="button" className="pos-pick-close" aria-label={t("positions.close")} onClick={() => setPick(null)}>
                        <Icon name="close" size={14} />
                      </button>
                    </div>
                    <div className="pos-pick-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-small"
                        onClick={() => {
                          if (meId) setMain(meId, pick.place);
                          setPick(null);
                        }}
                      >
                        {t("positions.playHere")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={() => {
                          if (meId) addBackup(meId, pick.place);
                          setPick(null);
                        }}
                      >
                        {t("positions.asBackup")}
                      </button>
                    </div>
                  </div>
                </AtPoint>
              )}
            </Radar>
            {hasLowerLevel(map) && (
              <div className="map-overlay map-overlay-bottom glass">
                <div className="segmented" role="group" aria-label={t("level.label")}>
                  {(["upper", "lower"] as const).map((l) => (
                    <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                      {t(`level.${l}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="positions-side">
            <ul className="positions-roster" aria-label={t("positions.players", { side: sideName })}>
              {team.members.map((m) => {
                const p = posOf(m.userId);
                const main = p ? placeOf(p.spotKey, p.spot) : null;
                const typed = p && !main && p.spot ? p.spot : null;
                const mine = canEdit(m.userId);
                return (
                  <li key={m.userId} className={`pos-row${m.userId === meId ? " is-me" : ""}`} style={{ ["--who" as string]: colourOf(m.userId) }}>
                    <span
                      className={`pos-face is-big${mine ? " is-draggable" : ""}`}
                      title={mine ? t("positions.dragHint") : m.name}
                      aria-hidden="true"
                      {...dragProps(m.userId)}
                    >
                      <Avatar name={m.name} url={m.avatarUrl} size={36} />
                    </span>
                    <div className="pos-row-body">
                      <div className="pos-row-name">
                        <b>{m.name}</b>
                        {m.userId === meId && <span className="muted small">({t("live.you")})</span>}
                      </div>
                      <div className="pos-chips">
                        {main ? (
                          <span className="pos-chip is-main">
                            {siteOf(main) && <span className={`pos-area area-${siteOf(main)}`}>{t(`areaShort.${siteOf(main)}`)}</span>}
                            {main.name}
                            {mine && (
                              <button type="button" className="pos-chip-x" aria-label={t("positions.removeSpot", { place: main.name, who: m.name })} onClick={() => put(m.userId, { main: null })}>
                                <Icon name="close" size={11} />
                              </button>
                            )}
                          </span>
                        ) : typed ? (
                          <span className="pos-chip is-typed" title={t("positions.typedHelp")}>
                            {typed} · {t("positions.notOnMap")}
                          </span>
                        ) : (
                          <span className="muted small">{mine ? t("positions.dragHere") : t("positions.notSet")}</span>
                        )}
                        {p?.backups.map((b) => (
                          <span key={b.key} className="pos-chip is-backup">
                            {b.name}
                            {mine && (
                              <button
                                type="button"
                                className="pos-chip-x"
                                aria-label={t("positions.removeSpot", { place: b.name, who: m.name })}
                                onClick={() => put(m.userId, { backups: p.backups.filter((x) => x.key !== b.key) })}
                              >
                                <Icon name="close" size={11} />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {mine && (
                        <div className="pos-lists">
                          <Select
                            label={t("positions.mainFor", { who: m.name, side: sideName })}
                            value={main?.id ?? null}
                            placeholder={t("positions.pickMain")}
                            groups={spotGroups(null)}
                            onChange={(v) => {
                              const place = byId.get(v);
                              if (place) setMain(m.userId, place);
                            }}
                          />
                          {(p?.backups.length ?? 0) < MAX_BACKUP_SPOTS && (
                            <Select
                              label={t("positions.backupFor", { who: m.name, side: sideName })}
                              value={null}
                              placeholder={t("positions.addBackup")}
                              buttonIcon={<Icon name="plus" size={14} />}
                              groups={spotGroups(main?.id ?? null)}
                              onChange={(v) => {
                                const place = byId.get(v);
                                if (place) addBackup(m.userId, place);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="positions-spread small" aria-label={t("positions.spread")}>
              {PREFERRED_AREAS.map((a) => (
                <span key={a} className={`pos-count${count(a) === 0 ? " is-empty" : ""}`}>
                  {t(`areaShort.${a}`)} {count(a)}
                </span>
              ))}
              {unset > 0 && <span className="muted">· {t("positions.unset", { count: unset })}</span>}
              {empty.map((a) => (
                <span key={a} className="pos-warn">
                  {t("positions.nobodyOn", { site: t(`area.${a}`), side: sideName })}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {save.error && <p className="field-error small">{errorText(t, save.error)}</p>}
      {drag &&
        createPortal(
          <div className="pos-ghost" style={{ left: drag.x, top: drag.y, ["--who" as string]: colourOf(drag.userId) }} aria-hidden="true">
            <span className="pos-face">
              <Avatar name={nameOf(drag.userId)} url={team.members.find((m) => m.userId === drag.userId)?.avatarUrl} size={30} />
            </span>
            <span className="pos-ghost-label">{drag.over ? drag.over.name : t("positions.dropOnMap")}</span>
          </div>,
          document.body,
        )}
    </section>
  );
}
